import React, { useRef, useEffect, useState } from 'react'
import loadable from '@loadable/component'
import SectionHeader from '../molecules/SectionHeader'
import { XLARGE, MEDIUM, Desktop, Mobile } from '../util/breakpoints'
import { PROMO, OFFER, SPECIAL_EVENT } from '../../api/contentTypes'
import Offer from '../organisms/Offer'
import Promo from '../organisms/Promo'
import { markdownToJSX } from '../../util/transforms'
import ReadMore from '../molecules/ReadMore'
import SpecialEvent from './SpecialEvent'
import useMediaQuery from '../util/useMediaQuery'
import Image from '../atoms/Image'
import 'react-multi-carousel/lib/styles.css'

const MultiCarousel = loadable(() => import( 'react-multi-carousel' ))
const Carousel = loadable(() => import( 'react-responsive-carousel' ), { resolveComponent: (components) => components.Carousel} )

export default ({ label, title, intro, carousel = [], hasBackground = false, carouselColumns = 3, textCenter, link, linkText, translations }) => {
  const numberOfCards = parseInt(carouselColumns)
  const siblingVisibilityPx = (carousel.length <= 1) ? 0 : 16

  const responsive = {
    desktop: {
      breakpoint: { max: 3000, min: XLARGE },
      items: numberOfCards
    },
    tablet: {
      breakpoint: { max: XLARGE, min: MEDIUM },
      items: 2
    },
    mobile: {
      breakpoint: { max: MEDIUM, min: 0 },
      items: 1,
      partialVisibilityGutter: siblingVisibilityPx * 2 // this is needed to tell the amount of px that should be visible.
    }
  }

  const isDesktop = useMediaQuery({ minWidth: MEDIUM })

  const carouselElements = carousel.map(({
                                           title,
                                           contentType,
                                           label,
                                           image,
                                           tag,
                                           backgroundImage,
                                           link,
                                           linkText,
                                           logo,
                                           description,
                                           price,
                                           alignLeft,
                                           modalText,
                                           modalImage,
                                           modalReadMore
                                         }, index) => {
    switch (contentType) {
      case OFFER :
        return (
          <Offer key={ index }
                 title={ title }
                 image={ image }
                 link={ link }
                 linkText={ linkText }
                 description={ description }
                 modalText={ modalText }
                 modalImage={ modalImage }
                 modalReadMore={ modalReadMore }
                 translations={translations}
          />
        )
      case PROMO :
        return (
          <Promo key={ index }
                 title={ title }
                 label={ label }
                 image={ backgroundImage }
                 logo={ logo }
                 link={ link }
                 price={ price }
                 alignLeft={ alignLeft }
                 modalText={ modalText }
                 modalImage={ modalImage }
                 translations={translations}
          />
        )
      case SPECIAL_EVENT :
        return (
          <SpecialEvent
            key={ index }
            tag={ tag }
            title={ title }
            desc={ description }
            image={ image }
            link={ link }
            translations={translations}
          />
        )
    }
  })

  const LinkContainer = () =>
    <div className={ 'u-container' + (textCenter ? ' u-center-text' : '') }>
      <ReadMore link={ link } text={ link.title }/>
    </div>

  const [additionalTransform, setAdditionalTransform] = useState(siblingVisibilityPx)

  return (
    <section
      className={ hasBackground ? 'u-background u-background--gradient u-background--pattern-top u-section-spacing' : 'u-section-spacing' }>
      <SectionHeader title={ markdownToJSX(title) } label={ label } paragraph={ intro } isCentred={ textCenter }
                     wide={ !textCenter }/>
      <div className="o-content-carousel">
        <Desktop>
          <MultiCarousel
            containerClass={
              'o-content-carousel__container o-carousel' +
              (carouselElements.length < numberOfCards && textCenter ? ' o-content-carousel__container--center' : '')
            }
            itemClass="o-content-carousel__item o-carousel"
            partialVisible={ true }
            responsive={ responsive }
            additionalTransfrom={ isDesktop ? 0 : additionalTransform }
            arrows
            centerMode={ false }
            draggable={ false }
            focusOnSelect={ false }
            infinite={ false }
            keyBoardControl
            minimumTouchDrag={ 20 }
            renderButtonGroupOutside={ false }
            renderDotsOutside={ false }
            showDots={ false }
            slidesToSlide={ 1 }
            swipeable
            beforeChange={ (nextSlide, { currentSlide, onMove }) => {
              if (isDesktop) return

              if (nextSlide === carouselElements.length - 1) {
                setAdditionalTransform(-siblingVisibilityPx)
              } else if (additionalTransform !== siblingVisibilityPx) {
                setAdditionalTransform(siblingVisibilityPx)
              }
            }}
          >
            { carouselElements }
          </MultiCarousel>
        </Desktop>
        <Mobile>
          <div className={ 'o-content-carousel__container o-carousel' +
          (carouselElements.length < numberOfCards && textCenter ? ' o-content-carousel__container--center' : '') }>
            <Carousel
              showThumbs={ false }
              centerMode={ true }
              centerSlidePercentage={ 90 }
              showArrows={ false }
              showStatus={ false }
              renderIndicator={ false }
              renderItem={ (item) => {
                return (
                  <div className="o-content-carousel__item o-carousel">
                    { item }
                  </div>
                )
              }}
            >
              { carouselElements }
            </Carousel>
          </div>
        </Mobile>
      </div>
      { link && <LinkContainer/> }
    </section>
  )
}
